import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';

export default function VenueFeatureModel({ open, onClose, featureId }) {
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const bearerToken = localStorage.getItem('token');
    const [isLoading, setIsLoading] = useState(false);
    const modalRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        if (open) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [open, onClose]);

    const handleClose = () => {
        onClose();
        newFeature.resetForm();
    };

    useEffect(() => {
        const fetchVenueFeature = async () => {
            if (!featureId) return; // Prevent running if no ID
            try {
                const response = await axios.get(`${apiUrl}event-service/venue-feature/${featureId}`, {
                    headers: { 'accept': 'application/json' }
                });

                if (response.data.success && response.data.data) {
                    const featureData = response.data.data;
                    newFeature.setValues({
                        name: featureData.name || '',
                    });
                }
            } catch (error) {
                console.error('Error fetching venue features:', error.message);
            }
        };

        if (open) {
            fetchVenueFeature();
        }
    }, [featureId, open]);

    const onSubmit = async (values) => {
        setIsLoading(true);
        try {
            if (featureId) {
                await axios.put(`${apiUrl}event-service/venue-feature/${featureId}`, values, {
                    headers: {
                        'Authorization': `Bearer ${bearerToken}`,
                        'Content-Type': 'application/json',
                        'accept': 'application/json',
                    },
                });
            } else {
                const payload = {
                    ...values,
                }
                await axios.post(`${apiUrl}event-service/venue-feature`, payload, {
                    headers: {
                        'Authorization': `Bearer ${bearerToken}`,
                        'Content-Type': 'application/json',
                        'accept': 'application/json',
                    },
                });
            }
        } catch (error) {
            console.error('Error inserting data:', error.message);
        } finally {
            setIsLoading(false);
            handleClose();
        }
    };

    const newFeature = useFormik({
        initialValues: {
            name: '',
        },
        validationSchema: Yup.object({
            name: Yup.string().required('Required'),
        }),
        onSubmit,
    });

    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center p-4 z-50">
            {/* Background overlay */}
            <div className="absolute inset-0 bg-black opacity-30" onClick={handleClose}></div>

            {/* Modal */}
            <div className="bg-white rounded-lg w-full max-w-xl z-50 relative">
                {/* Header */}
                <div className="bg-blue-500 text-white py-3 px-6 rounded-lg w-3/4 mx-auto relative z-10 -mt-6">
                    <h2 className="text-xl font-semibold text-center">Venue Feature</h2>
                </div>

                <div className="relative bg-white p-6 space-y-4 shadow-md rounded-b-lg">
                    <form onSubmit={newFeature.handleSubmit} className="space-y-4">
                        {/* Name and Status Row */}
                        <div className="flex flex-col sm:flex-row sm:items-end gap-4">
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Name
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    placeholder="Venue Feature Name"
                                    value={newFeature.values.name}
                                    onChange={newFeature.handleChange}
                                    onBlur={newFeature.handleBlur}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                {newFeature.touched.name && newFeature.errors.name && (
                                    <div className="text-red-500 text-xs mt-1">{newFeature.errors.name}</div>
                                )}
                            </div>
                        </div>

                        <div className="flex justify-end">
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="px-5 h-8 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                            >
                                {isLoading ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                        {isLoading && (
                                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                                    <div className="bg-white p-4 rounded-lg flex flex-col items-center shadow-lg">
                                        <svg
                                            className="animate-spin h-8 w-8 text-blue-600 mb-3"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            ></circle>
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                            ></path>
                                        </svg>

                                        <p className="text-sm font-medium text-gray-700">Saving...</p>
                                    </div>
                                </div>
                            )}

                    </form>
                </div>
            </div>
        </div>
    );
}

VenueFeatureModel.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    featureId: PropTypes.string,
};